import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import type { Notification } from '@/types/notification';
import Ionicons from '@expo/vector-icons/Ionicons';
import React from 'react';
import { View } from 'react-native';

// -- Single below-content action zone (buttons + status badges) --
//
// Every notification type that carries an action renders it here, below the
// content and any preview, so the action zone lives in one consistent place
// (never in the timestamp column). Friend-request and follow actions share the
// same shape, height, and rhythm via the design-system Button.

interface NotificationActionsProps {
  notification: Notification;
  canRespondToFriendRequest: boolean;
  onAcceptFriendRequest?: (notificationId: string, requestId: string) => void;
  onRejectFriendRequest?: (notificationId: string, requestId: string) => void;
  isProcessingFriendRequest?: boolean;
}

export function NotificationActions({
  notification,
  canRespondToFriendRequest,
  onAcceptFriendRequest,
  onRejectFriendRequest,
  isProcessingFriendRequest = false,
}: NotificationActionsProps) {
  if (notification.type === 'friend_request') {
    const canRespond = canRespondToFriendRequest && !notification.friendRequestAction;

    if (canRespond) {
      return (
        <FriendRequestButtons
          notification={notification}
          onAcceptFriendRequest={onAcceptFriendRequest}
          onRejectFriendRequest={onRejectFriendRequest}
          isProcessingFriendRequest={isProcessingFriendRequest}
        />
      );
    }

    return (
      <FriendRequestStatusBadge
        notification={notification}
        canRespond={canRespondToFriendRequest}
      />
    );
  }

  if (notification.type === 'follow') {
    return <FollowActions />;
  }

  return null;
}

// -- Private sub-components --

interface FriendRequestButtonsProps {
  notification: Notification;
  onAcceptFriendRequest?: (notificationId: string, requestId: string) => void;
  onRejectFriendRequest?: (notificationId: string, requestId: string) => void;
  isProcessingFriendRequest: boolean;
}

function FriendRequestButtons({
  notification,
  onAcceptFriendRequest,
  onRejectFriendRequest,
  isProcessingFriendRequest,
}: FriendRequestButtonsProps) {
  const requesterId = notification.user?.id;

  // sm buttons are 36px tall; extend the touchable area vertically to 48px so
  // the target meets Android 48dp / iOS 44pt without enlarging the visual. Only
  // vertical slop, so adjacent buttons don't overlap across the 8px gap.
  const touchSlop = { top: 6, bottom: 6 };

  return (
    <View className="mt-3 flex-row gap-2">
      <Button
        size="sm"
        hitSlop={touchSlop}
        loading={isProcessingFriendRequest}
        disabled={isProcessingFriendRequest}
        onPress={() => {
          if (onAcceptFriendRequest && requesterId) {
            onAcceptFriendRequest(notification.id, requesterId);
          }
        }}
      >
        Accept
      </Button>
      <Button
        size="sm"
        variant="secondary"
        hitSlop={touchSlop}
        disabled={isProcessingFriendRequest}
        onPress={() => {
          if (onRejectFriendRequest && requesterId) {
            onRejectFriendRequest(notification.id, requesterId);
          }
        }}
      >
        Ignore
      </Button>
    </View>
  );
}

function FriendRequestStatusBadge({
  notification,
  canRespond,
}: {
  notification: Notification;
  canRespond: boolean;
}) {
  const hasAction = notification.friendRequestAction;
  const showBadge = hasAction || !canRespond;

  if (!showBadge) return null;

  const accepted = hasAction === 'accepted';
  const rejected = hasAction === 'rejected';

  return (
    <View className="mt-3 flex-row items-center">
      <Ionicons
        name={accepted ? 'checkmark-circle' : rejected ? 'close-circle' : 'information-circle'}
        size={16}
        className={accepted ? 'text-primary' : 'text-muted-foreground'}
      />
      <Text className="ml-2 text-sm text-muted-foreground">
        {accepted
          ? 'Friend request accepted'
          : rejected
            ? 'Friend request ignored'
            : 'Friend request handled'}
      </Text>
    </View>
  );
}

function FollowActions() {
  const touchSlop = { top: 6, bottom: 6 };

  return (
    <View className="mt-3 flex-row gap-2">
      <Button size="sm" hitSlop={touchSlop}>
        Follow Back
      </Button>
      <Button size="sm" variant="secondary" hitSlop={touchSlop}>
        Ignore
      </Button>
    </View>
  );
}
